// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * Navigation inventory for the University Control Center.
 *
 * The areas below are exactly the subpaths `docs/04-REPOSITORY_GUIDE.md`
 * (lines 1040-1051) requires for `ui/apps/education/control-plane/`, with
 * `tenants/` replacing the pre-ADR-0051 `colleges/` name. `CST-006` forbids
 * omitting a required descendant from an implementation scope, so this list is
 * the structural inventory as well as the menu.
 */

import { capabilities } from '@eai/ui-shared';

/** One navigable administration area. */
export interface NavigationItem {
  /** Route path. */
  readonly path: string;
  /** Menu label. */
  readonly label: string;
  /** Short description shown under the label. */
  readonly description: string;
  /** Capability the session must have been offered for the area to be listed. */
  readonly requiredCapability: string;
  /** Repository-guide directory that owns the area. */
  readonly directory: string;
}

/** The required control-plane areas, in menu order. */
export const navigationItems: readonly NavigationItem[] = [
  {
    path: '/',
    label: 'Dashboard',
    description: 'Platform posture and open work',
    requiredCapability: capabilities.tenantsRead,
    directory: 'dashboard',
  },
  {
    path: '/tenants',
    label: 'Tenants',
    description: 'Registered colleges and their status',
    requiredCapability: capabilities.tenantsRead,
    directory: 'tenants',
  },
  {
    path: '/settings/ai-models',
    label: 'Settings — AI models',
    description: 'Providers, models and fallback order',
    requiredCapability: capabilities.aiSettingsRead,
    directory: 'settings',
  },
  {
    // ADR-0080. Beside AI models under Settings, and gated on its own
    // capability: an administrator who may curate models is not thereby
    // entitled to see what every college spent.
    path: '/settings/ai-usage',
    label: 'Settings — AI usage & cost',
    description: 'Estimated cost by college, provider and model',
    requiredCapability: capabilities.aiUsageRead,
    directory: 'settings',
  },
  {
    path: '/ai-services',
    label: 'AI services',
    description: 'Registered agents and MCP servers',
    requiredCapability: capabilities.aiServicesRead,
    directory: 'ai-services',
  },
  {
    path: '/knowledge',
    label: 'Knowledge',
    description: 'Index generations and completeness',
    requiredCapability: capabilities.knowledgeRead,
    directory: 'knowledge',
  },
  {
    path: '/documents',
    label: 'Documents',
    description: 'Managed knowledge documents',
    requiredCapability: capabilities.documentsRead,
    directory: 'documents',
  },
  {
    path: '/governance',
    label: 'Governance',
    description: 'Policies and pending approvals',
    requiredCapability: capabilities.governanceRead,
    directory: 'governance',
  },
  {
    path: '/analytics',
    label: 'Analytics',
    description: 'Platform metrics and cost signals',
    requiredCapability: capabilities.analyticsRead,
    directory: 'analytics',
  },
  {
    path: '/audit',
    label: 'Audit',
    description: 'Attributable administrative facts',
    requiredCapability: capabilities.auditRead,
    directory: 'audit',
  },
  {
    path: '/monitoring',
    label: 'Monitoring',
    description: 'Service health and runbooks',
    requiredCapability: capabilities.monitoringRead,
    directory: 'monitoring',
  },
  {
    path: '/access',
    label: 'Access',
    description: 'Roles, delegation, and break-glass grants',
    requiredCapability: capabilities.accessRead,
    directory: 'access',
  },
  {
    path: '/memory',
    label: 'Memory',
    description: 'Durable memory records and retention',
    requiredCapability: capabilities.memoryRead,
    directory: 'memory',
  },
];
